/** @format */
import { MarketCode } from '@toucan/client/global/market';
import {
    LegacyProductImage,
    Observation,
    PriceModifier,
    ProductState,
} from '@toucan/client/global/product';

export enum ProductConditionValue {
    UNKNOWN = 'UNKNOWN',
    NEW = 'NEW',
    LIKE_NEW = 'LIKE_NEW',
    EXCELLENT = 'EXCELLENT',
    GOOD = 'GOOD',
    WELL_USED = 'WELL_USED',
    HEAVILY_USED = 'HEAVILY_USED',
    FAULTY = 'FAULTY',

    // Legacy MBPX conditions - required for old APIs we are still using
    SR = 'SR',
    'S/R' = 'S/R',
    'LIKE-NEW' = 'LIKE-NEW',
    'WELL-USED' = 'WELL-USED',
    'HEAVILY-USED' = 'HEAVILY-USED',
}

export type ProductFormattedAttribute = {
    name: string;
    content: string;
};

export type Product = {
    /**
     * A pretty name for the product
     */
    name: string;

    /**
     * The product id
     */
    id?: number;

    /**
     * The model id
     */
    modelId?: number;

    /**
     * An optional model slug for the product
     */
    modelSlug?: string;

    /**
     * Product Image
     */
    image: string;

    /**
     * Product sku number
     */
    sku: string;

    /**
     * A description of the product
     */
    description: string;

    /**
     * Product price
     */
    price: string;

    /**
     * Unformatted product price
     */
    value?: number;

    /**
     * An optional discounted price.
     */
    discountPrice?: string;

    /**
     * An optional price addendum allows additional information to be provided
     * regarding a price. For example, a monthly cost breakdown
     */
    priceAddendum?: string;

    /**
     * The condition of the product
     */
    condition: ProductConditionValue;

    /**
     * An optional product condition description.
     */
    conditionDescription?: string;

    /**
     * Product page link
     */
    linkHref: string;

    /**
     * Product template path
     */
    linkTemplatePath?: string;

    /**
     * A state for the product, used to decide whether a product is available
     * for purchase
     */
    state?: ProductState;

    /**
     * A product's valid attributes e.g. shutter count, charges, hours
     */
    attributes?: ProductFormattedAttribute[];

    /**
     * A product's price modifiers
     */
    priceModifiers?: PriceModifier[];

    /**
     * Internal Images
     */
    internalImages?: LegacyProductImage[];

    /**
     * Observations
     */
    observations?: Observation[];

    /**
     * Custom description - usually just for s/rs
     */
    customDescription?: string;
    tag?: string;
    productObservationCount?: number;
};

export type ProductWithId = Product & { id: number };

export type SavedProduct = Pick<
    Product,
    | 'condition'
    | 'description'
    | 'modelId'
    | 'name'
    | 'sku'
    | 'state'
    | 'linkHref'
    | 'image'
    | 'price'
    | 'value'
> & {
    id: number;
    images: Omit<LegacyProductImage, 'meta'>[];
    listPrice: string;
    modelSlug: string;
    market: MarketCode;
};

export type ItemId = string | number | undefined;

export enum StockAlertPageType {
    PRODUCT = 'PRODUCT',
    MODEL = 'MODEL',
    LISTING = 'LISTING',
    OTHER = 'OTHER',
}

export enum AttributeKey {
    UNDEFINED_REASON = 'UNDEFINED_REASON',
    SHUTTER_COUNT = 'SHUTTER_COUNT',
    BATTERY_CHARGE_COUNT = 'BATTERY_CHARGE_COUNT',
    FIRMWARE = 'FIRMWARE',
    HOUR_COUNT = 'HOUR_COUNT',
}
